import React, { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import axios from 'axios'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import {
  Alert, type AlertColor, Button, FormControl, InputLabel, MenuItem, Select, Snackbar, Stack, TextField,
  Typography, FormHelperText, Card, CardContent, Grid, useTheme, useMediaQuery,
} from '@mui/material'
import EastIcon from '@mui/icons-material/East'
import SouthIcon from '@mui/icons-material/South'
import { isEmptyString, isValidNumberString } from '../../utils/string-utils'
import {
  type ICoin, type ITransferAccount, type ITransferSubAccount, type ITransferSubmissionWithMain, type ITransferSubmissionWithoutMain,
} from '../../models/transfer/transfer-info-types-bitget'
import CenteredCardLayout from '../../layouts/centered-card-layout'
import Chip from '@mui/material/Chip'

enum TRANSFER_TYPE {
  MAIN_TO_SUB = 'Main Account to Sub-Account',
  SUB_TO_MAIN = 'Sub-Account to Main Account',
  SUB_TO_SUB = 'Sub-Account to Sub-Account'
}

enum ACCOUNT_TYPE {
  SPOT = 'spot',
  UNKNOWN = '-'
}

const ACCOUNT_TYPE_NUMERIC_VALUES = new Map<ACCOUNT_TYPE, string>()
ACCOUNT_TYPE_NUMERIC_VALUES.set(ACCOUNT_TYPE.SPOT, 'spot')
ACCOUNT_TYPE_NUMERIC_VALUES.set(ACCOUNT_TYPE.UNKNOWN, '-')

function createResponseForTransferInvolvingMain (
  transferType: TRANSFER_TYPE,
  mainAccount: string, targetAccount: string, sourceAccountType: string, targetAccountType: string,
  coin: string, amount: string): ITransferSubmissionWithMain {
  return {
    masterAccount: mainAccount,
    type: '1',
    internalTransferType: transferType === TRANSFER_TYPE.MAIN_TO_SUB ? '1' : '2',
    from: sourceAccountType,
    to: targetAccountType,
    subAcct: targetAccount,
    ccy: coin,
    amt: amount,
  }
}

function createResponseForTransferNotInvolvingMain (
  mainAccount: string,
  sourceAccount: string, targetAccount: string, sourceAccountType: string, targetAccountType: string,
  coin: string, amount: string): ITransferSubmissionWithoutMain {
  return {
    masterAccount: mainAccount,
    type: '2',
    from: sourceAccountType,
    to: targetAccountType,
    fromSubAccount: sourceAccount,
    toSubAccount: targetAccount,
    ccy: coin,
    amt: amount,
  }
}

const TransferViewBitget: React.FC = () => {
  const theme = useTheme()
  const isLargerThanMD = useMediaQuery(theme.breakpoints.up('md'))

  const { data, error } = useSWR<ITransferAccount[], boolean>(
    process.env.REACT_APP_API_TRANSFER_BITGET_INDEX,
    basicFetcher,
    {
      shouldRetryOnError: false,
    },
  )

  const [mainAccountsMap, setMainAccountsMap] = useState<Map<string, ITransferAccount>>(new Map())
  const [mainToSubAccountsMap, setMainToSubAccountsMap] = useState<Map<string, Map<string, ITransferSubAccount>>>(new Map())

  useEffect(() => {
    let mainAccounts: ITransferAccount[]
    if (error === true || data === undefined) {
      mainAccounts = []
    } else {
      mainAccounts = data
    }

    const mainAccountsMap = new Map<string, ITransferAccount>()
    const subAccountsMap = new Map<string, Map<string, ITransferSubAccount>>()

    for (const mainAccount of mainAccounts) {
      if (isEmptyString(mainAccount.main_account)) {
        console.error('[Transfer] Empty main account')
        continue
      }

      const mainAccountName = String(mainAccount.main_account)
      if (mainAccountsMap.has(mainAccountName)) {
        console.error(`[Transfer] Duplicate main account: ${mainAccountName}`)
        continue
      }
      mainAccountsMap.set(mainAccountName, mainAccount)

      if (!subAccountsMap.has(mainAccountName)) {
        subAccountsMap.set(mainAccountName, new Map<string, ITransferSubAccount>())
      }

      const subAccountMap = subAccountsMap.get(mainAccountName)
      const subAccounts = mainAccount.sub_accounts
      for (const subAccount of subAccounts) {
        const { sub_account_name: subAccountName } = subAccount
        subAccountMap?.set(subAccountName, subAccount)
      }
    }

    setMainAccountsMap(mainAccountsMap)
    setMainToSubAccountsMap(subAccountsMap)
  }, [data, error])

  // Main
  const [mainAccount, setMainAccount] = useState<string>('')
  const [transferType, setTransferType] = useState<TRANSFER_TYPE>(TRANSFER_TYPE.MAIN_TO_SUB)
  // Source
  const [sourceAccount, setSourceAccount] = useState<string>('')
  const [sourceAccountType, setSourceAccountType] = useState<string>('')
  // Target
  const [targetAccount, setTargetAccount] = useState<string>('')
  const [targetAccountType, setTargetAccountType] = useState<string>('')
  // Transfer Options
  const [transferCoin, setTransferCoin] = useState<string>('')
  const [transferAmount, setTransferAmount] = useState<string>('')
  // Target helper text
  const [targetAccountHelperText, setTargetAccountHelperText] = useState<string>('select..')
  const [transferWarning, setTransferWarning] = useState<boolean>(false)

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')

  // submit confirm message
  const [showConfirmMsg, setShowConfirmMsg] = useState(false)

  const onSubmitConfirmMsg = (event: React.FormEvent): void => {
    event.preventDefault()
    setShowConfirmMsg(!showConfirmMsg)
  }

  const displayToastWithMessage = (message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }

  useEffect(() => {
    // sourceAccount, sourceAccountType, targetAccount and targetAccountType have been reset prior to this
    switch (transferType) {
      case TRANSFER_TYPE.MAIN_TO_SUB: {
        setSourceAccount(mainAccount)
        // Set initial account_type from first available coin
        const mainAccountData = mainAccountsMap.get(mainAccount)
        if (mainAccountData != null) {
          const firstCoin = mainAccountData.coin_list.find(coin => parseFloat(coin.amount) > 0)
          setSourceAccountType(firstCoin?.account_type ?? '')
        } else {
          setSourceAccountType('')
        }
        break
      }
      case TRANSFER_TYPE.SUB_TO_MAIN: {
        setTargetAccount(mainAccount)
        // Set initial account_type from first available coin
        const mainAccountDataForTarget = mainAccountsMap.get(mainAccount)
        if (mainAccountDataForTarget != null) {
          const firstCoinForTarget = mainAccountDataForTarget.coin_list.find(coin => parseFloat(coin.amount) > 0)
          setTargetAccountType(firstCoinForTarget?.account_type ?? '')
        } else {
          setTargetAccountType('')
        }
        break
      }
      case TRANSFER_TYPE.SUB_TO_SUB:
        // No operation
        break
    }
  }, [transferType, mainAccount, mainAccountsMap])

  // Auto-update account_type when sourceAccount is set or coin is selected
  useEffect(() => {
    if (isEmptyString(sourceAccount)) {
      setSourceAccountType('')
      return
    }

    let actualType = ''
    let coinList: ICoin[] | undefined

    // Get the coin list for the source account
    if (sourceAccount === mainAccount) {
      const mainAccountData = mainAccountsMap.get(mainAccount)
      coinList = mainAccountData?.coin_list
    } else {
      const subAccountData = mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)
      coinList = subAccountData?.coin_list
    }

    if (coinList != null) {
      // If coin is selected, use that coin's account_type, otherwise use the first available coin's account_type
      if (!isEmptyString(transferCoin)) {
        const coinData = coinList.find(c => c.coin === transferCoin)
        actualType = coinData?.account_type ?? ''
      } else {
        const firstCoin = coinList.find(coin => parseFloat(coin.amount) > 0)
        actualType = firstCoin?.account_type ?? ''
      }
    }

    if (actualType !== sourceAccountType) {
      setSourceAccountType(actualType)
    }
  }, [sourceAccount, transferCoin, mainAccount, mainAccountsMap, mainToSubAccountsMap, sourceAccountType])

  useEffect(() => {
    if (isEmptyString(targetAccount)) {
      setTargetAccountType('')
      return
    }

    let actualType = ''
    let coinList: ICoin[] | undefined

    // Get the coin list for the target account
    if (targetAccount === mainAccount) {
      const mainAccountData = mainAccountsMap.get(mainAccount)
      coinList = mainAccountData?.coin_list
    } else {
      const subAccountData = mainToSubAccountsMap.get(mainAccount)?.get(targetAccount)
      coinList = subAccountData?.coin_list
    }

    if (coinList != null) {
      // If coin is selected, use that coin's account_type, otherwise use the first available coin's account_type
      if (!isEmptyString(transferCoin)) {
        const coinData = coinList.find(c => c.coin === transferCoin)
        actualType = coinData?.account_type ?? ''
      } else {
        const firstCoin = coinList.find(coin => parseFloat(coin.amount) > 0)
        actualType = firstCoin?.account_type ?? ''
      }
    }

    if (actualType !== targetAccountType) {
      setTargetAccountType(actualType)
    }
  }, [targetAccount, transferCoin, mainAccount, mainAccountsMap, mainToSubAccountsMap, targetAccountType])

  useEffect(() => {
    /** set a warning message if we are transferring between accounts of different family (for example, bitget_rlb1, exchange=bitget, family=rlb, id=1 */
    const regexp = /^(?<exchange>[a-z]+)_(?<family>[a-z]+)(?<id>\d+)?$/i

    const targetAccountMatch = targetAccount.match(regexp)
    const sourceAccountMatch = sourceAccount.match(regexp)

    if (isEmptyString(sourceAccount) || isEmptyString(targetAccount)) {
      setTargetAccountHelperText('select..')
      setTransferWarning(false)
    } else {
      // Check if either account is the main account
      const isSourceMainAccount = sourceAccount === mainAccount
      const isTargetMainAccount = targetAccount === mainAccount

      if (isSourceMainAccount || isTargetMainAccount) {
        // If either account is main account, check if the other is from the same exchange
        const otherAccount = isSourceMainAccount ? targetAccount : sourceAccount
        const otherMatch = otherAccount.match(regexp)
        if (otherMatch?.groups?.exchange === 'bitget') {
          setTargetAccountHelperText('same family transfer')
          setTransferWarning(false)
        } else {
          setTargetAccountHelperText('warning: cross-family transfer')
          setTransferWarning(true)
        }
      } else if (((sourceAccountMatch?.groups?.exchange) != null) && ((sourceAccountMatch?.groups?.family) != null) &&
        ((targetAccountMatch?.groups?.exchange) != null) && ((targetAccountMatch?.groups?.family) != null)) {
        if (sourceAccountMatch.groups.exchange === targetAccountMatch.groups.exchange && sourceAccountMatch.groups.family === targetAccountMatch.groups.family) {
          setTargetAccountHelperText('same family transfer')
          setTransferWarning(false)
        } else {
          setTargetAccountHelperText('warning: cross-family transfer')
          setTransferWarning(true)
        }
      } else {
        setTargetAccountHelperText('same family transfer')
        setTransferWarning(false)
      }
    }
  }, [sourceAccount, targetAccount, mainAccount])

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault()

    if (isEmptyString(process.env.REACT_APP_API_TRANSFER_BITGET_EXECUTE) || process.env.REACT_APP_API_TRANSFER_BITGET_EXECUTE == null) {
      console.error('[Transfer] Failed to execute transfer (reason: invalid execute url)')
      displayToastWithMessage('Transfer API endpoint not configured', 'error')
      setShowConfirmMsg(false)
      return
    }

    if (isEmptyString(mainAccount)) {
      displayToastWithMessage('Please select a main account', 'error')
      setShowConfirmMsg(false)
      return
    }

    if (isEmptyString(sourceAccount)) {
      displayToastWithMessage('Please select a source account', 'error')
      setShowConfirmMsg(false)
      return
    }

    if (isEmptyString(targetAccount)) {
      displayToastWithMessage('Please select a target account', 'error')
      setShowConfirmMsg(false)
      return
    }

    if (isEmptyString(transferCoin)) {
      displayToastWithMessage('Please select a coin to transfer', 'error')
      setShowConfirmMsg(false)
      return
    }

    if (isEmptyString(transferAmount) || !isValidNumberString(transferAmount)) {
      displayToastWithMessage('Please enter a valid amount', 'error')
      setShowConfirmMsg(false)
      return
    }

    if (sourceAccount === targetAccount) {
      displayToastWithMessage('Source account and target account cannot be the same account', 'error')
      setShowConfirmMsg(false)
      return
    }

    // Get actual account_type from data
    const getActualAccountType = (account: string, coin: string): string => {
      if (account === mainAccount) {
        const mainAccountData = mainAccountsMap.get(mainAccount)
        if (mainAccountData == null) {
          return 'spot'
        }
        const coinData = mainAccountData.coin_list.find(c => c.coin === coin)
        return coinData?.account_type ?? 'spot'
      } else {
        const subAccountData = mainToSubAccountsMap.get(mainAccount)?.get(account)
        if (subAccountData == null) {
          return 'spot'
        }
        const coinData = subAccountData.coin_list.find(c => c.coin === coin)
        return coinData?.account_type ?? 'spot'
      }
    }

    const actualSourceAccountType = getActualAccountType(sourceAccount, transferCoin)
    const actualTargetAccountType = getActualAccountType(targetAccount, transferCoin)

    let body: ITransferSubmissionWithMain | ITransferSubmissionWithoutMain | undefined

    switch (transferType) {
      case TRANSFER_TYPE.MAIN_TO_SUB: {
        const mainToSubTargetAccount = mainToSubAccountsMap.get(mainAccount)?.get(targetAccount)
        if (mainToSubTargetAccount == null) {
          console.error('[Transfer] Failed to execute transfer (reason: sub-account does not exist)')
          return
        }
        body = createResponseForTransferInvolvingMain(
          transferType,
          mainAccount,
          mainToSubTargetAccount.sub_account_init_name,
          actualSourceAccountType,
          actualTargetAccountType,
          transferCoin,
          transferAmount,
        )
        break
      }
      case TRANSFER_TYPE.SUB_TO_MAIN: {
        const subToMainSourceAccount = mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)
        if (subToMainSourceAccount == null) {
          console.error('[Transfer] Failed to execute transfer (reason: sub-account does not exist)')
          return
        }
        body = createResponseForTransferInvolvingMain(
          transferType,
          mainAccount,
          subToMainSourceAccount.sub_account_init_name,
          actualSourceAccountType,
          actualTargetAccountType,
          transferCoin,
          transferAmount,
        )
        break
      }
      case TRANSFER_TYPE.SUB_TO_SUB: {
        const sourceSubAccount = mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)
        const targetSubAccount = mainToSubAccountsMap.get(mainAccount)?.get(targetAccount)
        if (sourceSubAccount == null || targetSubAccount == null) {
          console.error('[Transfer] Failed to execute transfer (reason: sub-account does not exist)')
          return
        }
        body = createResponseForTransferNotInvolvingMain(
          mainAccount,
          sourceSubAccount.sub_account_init_name,
          targetSubAccount.sub_account_init_name,
          actualSourceAccountType,
          actualTargetAccountType,
          transferCoin,
          transferAmount,
        )
        break
      }
    }

    if (body == null) {
      console.error('[Transfer] Failed to assemble request body')
      displayToastWithMessage('Failed to assemble request body', 'error')
      setShowConfirmMsg(false)
      return
    }

    console.debug('[Transfer] Bitget request body:', JSON.stringify(body, null, 2))

    axios
      .post(process.env.REACT_APP_API_TRANSFER_BITGET_EXECUTE, body)
      .then(response => {
        if (Object.hasOwn(response, 'data')) {
          const responseData = response.data
          if (responseData?.success === '1') {
            const transactionId = responseData?.transaction_id ?? 'N/A'
            const toastMessage = `Transfer Succeeded\nSuccess: ${responseData.success}\nTransaction ID: ${transactionId}`
            displayToastWithMessage(toastMessage, 'success')
          } else {
            const toastMessage = `Transfer Completed\nResponse: ${JSON.stringify(responseData)}`
            displayToastWithMessage(toastMessage, 'success')
          }
        } else {
          displayToastWithMessage('Transfer completed but no data received', 'success')
        }
      })
      .catch(reason => {
        let toastMessage
        if (reason.response?.data != null) {
          const errorData = reason.response.data
          // Bitget API might return different error format
          if (errorData.success != null) {
            toastMessage = `Transfer Failed\nSuccess: ${errorData.success}\nTransaction ID: ${errorData.transaction_id ?? 'N/A'}`
          } else if (errorData.error != null) {
            // Handle Bitget error format: {"error":"Transfer ERROR","error_execution":"..."}
            const errorMsg = errorData.error
            const errorExecution = errorData.error_execution ?? errorData.errorExecution ?? ''
            toastMessage = `Transfer Failed\nError: ${errorMsg}${errorExecution !== '' ? `\nDetails: ${errorExecution}` : ''}`
          } else if (errorData.code != null) {
            toastMessage = `Transfer Failed\nCode: ${errorData.code}\nData: ${errorData.data ?? 'N/A'}\nMsg: ${errorData.msg ?? 'N/A'}`
          } else {
            toastMessage = `Transfer Failed\n${JSON.stringify(errorData)}`
          }
        } else {
          toastMessage = 'Transfer Failed. Please check console for details.'
        }
        displayToastWithMessage(toastMessage, 'error')
      })
      .finally(() => {
        setShowConfirmMsg(false)
      })
  }

  const availableCoins = useMemo(() => {
    if (isEmptyString(sourceAccount)) {
      return []
    }

    if (sourceAccount === mainAccount) {
      const mainAccountData = mainAccountsMap.get(mainAccount)
      if (mainAccountData == null) {
        return []
      }
      return mainAccountData.coin_list
        .filter(coin => parseFloat(coin.amount) > 0)
        .map(coin => coin.coin)
        .sort()
    } else {
      const subAccountData = mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)
      if (subAccountData == null) {
        return []
      }
      return subAccountData.coin_list
        .filter(coin => parseFloat(coin.amount) > 0)
        .map(coin => coin.coin)
        .sort()
    }
  }, [sourceAccount, mainAccount, mainAccountsMap, mainToSubAccountsMap])

  const coinMenuItems = useMemo(() => {
    return availableCoins.map(coin => (
      <MenuItem key={coin} value={coin}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>{coin}</Typography>
          <Chip
            label={`${getCoinAmount(coin)} ${getCoinAccountType(coin)}`}
            size="small"
            variant="outlined"
          />
        </Stack>
      </MenuItem>
    ))
  }, [availableCoins])

  function getCoinAmount (coin: string): string {
    if (isEmptyString(sourceAccount)) {
      return '0'
    }

    if (sourceAccount === mainAccount) {
      const mainAccountData = mainAccountsMap.get(mainAccount)
      if (mainAccountData == null) {
        return '0'
      }
      const coinData = mainAccountData.coin_list.find(c => c.coin === coin)
      return coinData?.amount ?? '0'
    } else {
      const subAccountData = mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)
      if (subAccountData == null) {
        return '0'
      }
      const coinData = subAccountData.coin_list.find(c => c.coin === coin)
      return coinData?.amount ?? '0'
    }
  }

  function getCoinAccountType (coin: string): string {
    if (isEmptyString(sourceAccount)) {
      return 'spot'
    }

    if (sourceAccount === mainAccount) {
      const mainAccountData = mainAccountsMap.get(mainAccount)
      if (mainAccountData == null) {
        return 'spot'
      }
      const coinData = mainAccountData.coin_list.find(c => c.coin === coin)
      return coinData?.account_type ?? 'spot'
    } else {
      const subAccountData = mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)
      if (subAccountData == null) {
        return 'spot'
      }
      const coinData = subAccountData.coin_list.find(c => c.coin === coin)
      return coinData?.account_type ?? 'spot'
    }
  }

  if (error === true) {
    return (
      <CenteredCardLayout>
        <Alert severity="error">
          Failed to load transfer data. Please try again later.
        </Alert>
      </CenteredCardLayout>
    )
  }

  return (
    <CenteredCardLayout>
      <form id='transfer-form' onSubmit={onSubmitConfirmMsg}>
        <Typography gutterBottom variant="subtitle1">Bitget Transfer</Typography>
        <Stack gap={1} alignItems='center'>
          <Card variant='outlined' sx={{ width: 1, padding: 2 }}>
            <CardContent>
              <Stack gap={2}>
                <FormControl variant='standard'>
                  <InputLabel id='main-account-select-label'>Main Account</InputLabel>
                  <Select
                    labelId='main-account-select-label'
                    id='main-account-select'
                    label='Main Account'
                    value={mainAccount}
                    onChange={event => {
                      setMainAccount(event.target.value)
                      setSourceAccount('')
                      setTargetAccount('')
                      setTransferCoin('')
                      setTransferAmount('')
                    }}
                  >
                    {Array.from(mainAccountsMap.keys())
                      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                      .map(key => {
                        return <MenuItem key={key} value={key}>{key}</MenuItem>
                      })
                    }
                  </Select>
                </FormControl>

                <FormControl variant='standard' disabled={isEmptyString(mainAccount)}>
                  <InputLabel id='transfer-type-select-label'>Transfer Type</InputLabel>
                  <Select
                    labelId='transfer-type-select-label'
                    id='transfer-type-select'
                    label='Transfer Type'
                    value={transferType}
                    onChange={event => {
                      setTransferType(event.target.value as TRANSFER_TYPE)
                      setSourceAccount('')
                      setTargetAccount('')
                      setTransferCoin('')
                      setTransferAmount('')
                    }}
                  >
                    <MenuItem value={TRANSFER_TYPE.MAIN_TO_SUB}>Main Account to Sub-Account</MenuItem>
                    <MenuItem value={TRANSFER_TYPE.SUB_TO_MAIN}>Sub-Account to Main Account</MenuItem>
                    <MenuItem value={TRANSFER_TYPE.SUB_TO_SUB}>Sub-Account to Sub-Account</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>

          <Card variant='outlined' sx={{ width: 1, padding: 2 }}>
            <CardContent>
              <Grid container alignItems='center' spacing={4}>

                <Grid
                  item xs
                  display='flex'
                  flexDirection='column'
                  gap={2}
                >
                  <FormControl
                    variant='standard'
                    sx={{ minWidth: 300 }}
                    disabled={isEmptyString(mainAccount) || transferType === TRANSFER_TYPE.MAIN_TO_SUB}
                  >
                    <InputLabel id='source-account-select-label'>Source Account</InputLabel>
                    <Select
                      labelId='source-account-select-label'
                      id='source-account-select'
                      name='account'
                      label='Source Account'
                      value={sourceAccount}
                      onChange={event => {
                        setSourceAccount(event.target.value)
                        setTransferCoin('')
                        setTransferAmount('')
                      }}
                    >
                      {
                        transferType === TRANSFER_TYPE.MAIN_TO_SUB &&
                        <MenuItem key={mainAccount} value={mainAccount}>{mainAccount}</MenuItem>
                      }
                      {
                        (transferType === TRANSFER_TYPE.SUB_TO_MAIN || transferType === TRANSFER_TYPE.SUB_TO_SUB) &&
                        Array.from(mainToSubAccountsMap.get(mainAccount)?.keys() ?? [])
                          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                          .map(key => {
                            return <MenuItem key={key} value={key}>{key}</MenuItem>
                          })
                      }
                    </Select>
                    <FormHelperText id="source-account-helper-text"> </FormHelperText>
                  </FormControl>

                  <FormControl variant='standard' disabled={isEmptyString(mainAccount) || isEmptyString(transferCoin)}>
                    <InputLabel id='source-account-type-select-label'>Source Type</InputLabel>
                    <Select
                      labelId='source-account-type-select-label'
                      id='source-account-type-select'
                      label='Source Type'
                      value={sourceAccountType}
                      onChange={event => {
                        setSourceAccountType(event.target.value)
                      }}
                    >
                      {!isEmptyString(sourceAccountType) && (
                        <MenuItem value={sourceAccountType}>{sourceAccountType}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item xs={12} md='auto'
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  minWidth='60px'
                  gap={2}
                >
                  {
                    isLargerThanMD ? <EastIcon fontSize='large'/> : <SouthIcon fontSize='large'/>
                  }
                </Grid>

                <Grid
                  item xs
                  display='flex'
                  flexDirection='column'
                  gap={2}
                >
                  <FormControl
                    variant='standard'
                    sx={{ minWidth: 300 }}
                    disabled={isEmptyString(mainAccount) || transferType === TRANSFER_TYPE.SUB_TO_MAIN}
                  >
                    <InputLabel id='target-account-select-label'>Target Account</InputLabel>
                    <Select
                      labelId='target-account-select-label'
                      id='target-account-select'
                      name='account'
                      label='Target Account'
                      value={targetAccount}
                      onChange={event => {
                        setTargetAccount(event.target.value)
                      }}
                    >
                      {
                        transferType === TRANSFER_TYPE.SUB_TO_MAIN &&
                        <MenuItem key={mainAccount} value={mainAccount}>{mainAccount}</MenuItem>
                      }
                      {
                        Array.from(mainToSubAccountsMap.get(mainAccount)?.keys() ?? [])
                          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                          .map(key => {
                            return <MenuItem key={key} value={key}>{key}</MenuItem>
                          })
                      }
                    </Select>
                    <FormHelperText id="target-account-helper-text" error={transferWarning}>{targetAccountHelperText}</FormHelperText>
                  </FormControl>

                  <FormControl variant='standard' disabled={isEmptyString(mainAccount) || isEmptyString(transferCoin)}>
                    <InputLabel id='target-account-type-select-label'>Target Type</InputLabel>
                    <Select
                      labelId='target-account-type-select-label'
                      id='target-account-type-select'
                      label='Target Type'
                      value={targetAccountType}
                      onChange={event => {
                        setTargetAccountType(event.target.value)
                      }}
                    >
                      {!isEmptyString(targetAccountType) && (
                        <MenuItem value={targetAccountType}>{targetAccountType}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant='outlined' sx={{ width: 1, padding: 2 }}>
            <CardContent>
              <Stack gap={2}>
                <FormControl
                  variant='standard'
                  disabled={isEmptyString(sourceAccount)}
                  error={coinMenuItems.length === 0 && !isEmptyString(sourceAccount)}
                >
                  <InputLabel id='coin-select-label'>Coin</InputLabel>
                  <Select
                    labelId='coin-select-label'
                    id='coin-select'
                    label='Coin'
                    value={transferCoin}
                    onChange={event => {
                      setTransferCoin(event.target.value)
                      setTransferAmount('')
                    }}
                  >
                    {coinMenuItems}
                  </Select>
                  <FormHelperText>{
                    (coinMenuItems.length === 0 && !isEmptyString(sourceAccount))
                      ? `There are no valid coins to transfer out from '${sourceAccount}'.`
                      : null
                  }</FormHelperText>
                </FormControl>

                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`${getCoinAmount(transferCoin)} ${transferCoin}`}
                    variant="outlined"
                    onClick={event => {
                      setTransferAmount(getCoinAmount(transferCoin))
                    }}
                    onDelete={event => {
                      setTransferAmount('')
                    }}
                  />
                </Stack>

                <TextField
                  id='amount-text-field'
                  label='Amount'
                  type='number'
                  variant='standard'
                  sx={{ minHeight: '4.5rem' }}
                  disabled={isEmptyString(transferCoin)}
                  value={transferAmount}
                  onChange={event => {
                    setTransferAmount(event.target.value)
                  }}
                  helperText={
                    !isEmptyString(transferCoin) && !isEmptyString(getCoinAmount(transferCoin))
                      ? `Available: ${getCoinAmount(transferCoin)} ${transferCoin}`
                      : ''
                  }
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    isEmptyString(mainAccount) ||
                    isEmptyString(sourceAccount) ||
                    isEmptyString(targetAccount) ||
                    isEmptyString(transferCoin) ||
                    isEmptyString(transferAmount) ||
                    !isValidNumberString(transferAmount) ||
                    sourceAccount === targetAccount
                  }
                >
                  Submit
                </Button>

                {showConfirmMsg && (
                  <div>
                    <Stack spacing={2} direction="row">
                      <Alert severity="warning">Are you sure to submit this transfer?</Alert>
                      <Button variant='outlined' color="success" onClick={onSubmit}>Yes</Button>
                      <Button variant='outlined' color="error" onClick={event => { setShowConfirmMsg(false) }}>No</Button>
                    </Stack>
                  </div>
                )}

                <Typography
                  variant='caption'
                  color='error'
                  sx={{
                    display: (!isEmptyString(sourceAccount) &&
                      !isEmptyString(targetAccount) && sourceAccount === targetAccount)
                      ? 'inline'
                      : 'none',
                  }}
                >
                  Source account and target account cannot be the same account
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={60000}
          open={toastOpen}
          onClose={() => {
            setToastOpen(false)
          }}
        >
          <Alert
            sx={{ width: 1, fontSize: 'medium', whiteSpace: 'pre-line' }}
            variant='filled'
            onClose={() => {
              setToastOpen(false)
            }}
            severity={toastSeverity}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </form>
    </CenteredCardLayout>
  )
}

export default TransferViewBitget
